/**
 * SPA card path: renders the shared `CardFields` in the light DOM and tokenizes
 * via the setup-intent + `confirmCardSetup` flow, then attaches the method.
 * Must be rendered inside a Stripe `<Elements>` provider (see `CardEntry`).
 *
 * Split out of `PaymentPage.tsx` so the embed-vs-SPA routing in `CardEntry` can be
 * unit-tested in isolation (mocked to a sentinel) without dragging in real Stripe
 * Elements or the jotai/auth context this form needs (see `cardEntry.embed.test.tsx`).
 */
import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';

import { setPaymentMethod, setupIntent } from '../../../lib/api.ts';
import { track, resolveBookingType } from '@/lib/analytics/index.ts';
import {
  AuthAtom,
  reloadPaymentMethodsAtom,
  useRestaurantId,
  useUserDetails,
  useWidgetState,
  widgetPaymentMethodSelection,
} from '../lib/state.ts';
import { CardFields, type CardSubmitArgs } from './CardForm.tsx';

export function SpaCardForm({ onSuccess }: { onSuccess: (brand: string, last4: string) => void }) {
  const auth = useAtomValue(AuthAtom);
  const restaurantId = useRestaurantId();
  const reloadPaymentMethods = useSetAtom(reloadPaymentMethodsAtom);
  const user = useUserDetails();
  const [widget, updateWidget] = useWidgetState();

  const handleSubmit = async ({ stripe, cardNumberElement, billingDetails }: CardSubmitArgs) => {
    try {
      const response = await setupIntent(auth!.token);
      if (!response?.setupIntent) {
        Sentry.captureMessage('Widget: Problem occurred with api/setup-intent');
        return { error: 'Sorry, there was an issue. Please try again in a moment.' };
      }

      const { setupIntent: result, error } = await stripe.confirmCardSetup(
        response.setupIntent.client_secret,
        { payment_method: { card: cardNumberElement, billing_details: billingDetails } }
      );
      if (error) {
        return { error: error.message };
      }

      const payment_method = result?.payment_method as string;
      let cardBrand = 'Card';
      let cardLast4 = '****';
      if (result?.payment_method && typeof result.payment_method === 'object') {
        const pm = result.payment_method as { card?: { brand?: string; last4?: string } };
        cardBrand = pm.card?.brand ?? 'Card';
        cardLast4 = pm.card?.last4 ?? '****';
      }

      const dorsia_payment = await setPaymentMethod(auth!.token, {
        paymentMethodId: payment_method,
      });
      updateWidget(widgetPaymentMethodSelection(dorsia_payment.payment_id));

      if (restaurantId != null) {
        track('Widget Payment Method Added', {
          restaurant_id: restaurantId,
          booking_type: resolveBookingType(widget.bookingType),
          payment_type: 'card',
        });
      }

      reloadPaymentMethods();
      onSuccess(cardBrand, cardLast4);
      return undefined;
    } catch (e) {
      Sentry.captureException(e);
      return { error: 'Sorry, there was an issue. Please try again in a moment.' };
    }
  };

  return (
    <CardFields
      onSubmit={handleSubmit}
      defaultFirstName={user.first_name ?? ''}
      defaultLastName={user.last_name ?? ''}
      email={user.email}
    />
  );
}
